import { InteractionsTrigger } from '@wix/ambassador-devcenter-v1-component-type-data/types';
import type { EditorPointer } from '@wix/editorx-core-api';
import { CompRef } from '@wix/document-services-types';
import {
  EditActionsDictionary,
  NativeActionsList,
} from '@wix/editor-platform-host-integration-apis';
import { DATA, Action, CONTAINER_TYPE } from '@wix/component-protocol/schema';

import { hasDataType, isCustomizedNativeManifestAction } from '../utils';
import { ComponentsService } from '@wix/editor-platform-manifest-services';
import globalDesignActions from './globalDesign/actions';

const INTERACTION_TRIGGERS = Object.values(InteractionsTrigger);

type EditActionValidator = (
  componentsAPI: ComponentsService,
  editorPointer: EditorPointer<CompRef>,
) => boolean;

const validateNativeAction = (action?: Action) => {
  return !!action && !isCustomizedNativeManifestAction(action);
};

const validateInteractionAction = (
  componentsAPI: ComponentsService,
  editorPointer: EditorPointer<CompRef>,
) => {
  const interactions = componentsAPI.get.interactions(editorPointer);

  if (!interactions?.triggers?.length) {
    return false;
  }

  return interactions.triggers.every((trigger) =>
    INTERACTION_TRIGGERS.includes(trigger),
  );
};

const validateMediaAction = (
  componentsAPI: ComponentsService,
  editorPointer: EditorPointer<CompRef>,
) => {
  const mediaAction = componentsAPI.get.editActions(editorPointer)?.media;
  const hasMedia =
    componentsAPI.editActions.resolveDataItems(
      NativeActionsList.changeMedia,
      editorPointer,
    ).length > 0;
  return validateNativeAction(mediaAction) || (!mediaAction && hasMedia);
};

const validateHelpAction = (
  componentsAPI: ComponentsService,
  editorPointer: EditorPointer<CompRef>,
) => {
  const helpData = componentsAPI.get.help(editorPointer);
  return Boolean(helpData?.kbId || helpData?.url);
};

const validateImageAction = (
  componentsAPI: ComponentsService,
  editorPointer: EditorPointer<CompRef>,
) => {
  const mediaAction = componentsAPI.get.editActions(editorPointer)?.media;
  const hasMedia =
    componentsAPI.editActions.resolveDataItems(
      NativeActionsList.editImage,
      editorPointer,
    ).length > 0;
  return validateNativeAction(mediaAction) || (!mediaAction && hasMedia);
};

const validateBackgroundAction = (
  componentsAPI: ComponentsService,
  editorPointer: EditorPointer<CompRef>,
) => Boolean(componentsAPI.get.containerData(editorPointer));

const validateSettingsAction = (
  componentsAPI: ComponentsService,
  editorPointer: EditorPointer<CompRef>,
) => {
  const settingsAction = componentsAPI.get.editActions(editorPointer)?.settings;
  const hasData =
    componentsAPI.editActions.resolveDataItems(
      NativeActionsList.settings,
      editorPointer,
    ).length > 0;
  return validateNativeAction(settingsAction) || (!settingsAction && hasData);
};

const validateManageMenuAction = (
  componentsAPI: ComponentsService,
  editorPointer: EditorPointer<CompRef>,
) => {
  const manageMenuAction =
    componentsAPI.get.editActions(editorPointer)?.manageMenu;
  const hasData =
    componentsAPI.editActions.resolveDataItems(
      NativeActionsList.manageMenu,
      editorPointer,
    ).length > 0;

  return (
    validateNativeAction(manageMenuAction) || (!manageMenuAction && hasData)
  );
};

const validateManageItemsAction = (
  componentsAPI: ComponentsService,
  editorPointer: EditorPointer<CompRef>,
) => {
  const manageItemsAction =
    componentsAPI.get.editActions(editorPointer)?.manageItems;
  const hasData = hasDataType(
    componentsAPI.get.data(editorPointer),
    DATA.DATA_TYPE.arrayItems,
  );
  return (
    validateNativeAction(manageItemsAction) || (!manageItemsAction && hasData)
  );
};

const validateDesignAction = (
  componentsAPI: ComponentsService,
  editorPointer: EditorPointer<CompRef>,
) => {
  const designAction = componentsAPI.get.editActions(editorPointer)?.design;
  const style = componentsAPI.get.style(editorPointer);
  const hasStyle =
    Object.keys(style?.cssProperties || {}).length > 0 ||
    Object.keys(style?.cssCustomProperties || {}).length > 0;
  return validateNativeAction(designAction) || (!designAction && hasStyle);
};

const validateRichTextAction = (
  componentsAPI: ComponentsService,
  editorPointer: EditorPointer<CompRef>,
) =>
  hasDataType(componentsAPI.get.data(editorPointer), DATA.DATA_TYPE.richText);

const validatePluginsAction = (
  componentsAPI: ComponentsService,
  editorPointer: EditorPointer<CompRef>,
): boolean => {
  const context = componentsAPI.getContext(editorPointer);

  return context.getChildren().some((child) =>
    Object.values(child.resolver.getDataDefinitions()).some((dataItem) => {
      // TODO: Check csm for slot extension to filter non-public slots
      return (
        dataItem.dataType === DATA.DATA_TYPE.container &&
        dataItem.container?.containerType === CONTAINER_TYPE.slot
      );
    }),
  );
};
export const editActionsValidators: EditActionsDictionary<EditActionValidator> =
  {
    [NativeActionsList.presets]: (componentsAPI, editorPointer) =>
      componentsAPI.editActions.hasTemplates(editorPointer) ||
      Object.keys(
        componentsAPI.editActions.resolvePresetItems(
          NativeActionsList.presets,
          editorPointer,
        ),
      ).length > 1, // Product: in order to have MORE designs, component needs to have more than one preset (= design)
    [NativeActionsList.link]: (componentsAPI, editorPointer) =>
      componentsAPI.editActions.resolveDataItems(
        NativeActionsList.link,
        editorPointer,
      ).length > 0,
    [NativeActionsList.editText]: (componentsAPI, editorPointer) =>
      componentsAPI.editActions.resolveDataItems(
        NativeActionsList.editText,
        editorPointer,
      ).length > 0,
    [NativeActionsList.editRichText]: validateRichTextAction,
    [NativeActionsList.richTextAlignment]: validateRichTextAction,
    [NativeActionsList.richTextBold]: validateRichTextAction,
    [NativeActionsList.richTextDirection]: validateRichTextAction,
    [NativeActionsList.richTextFontFamily]: validateRichTextAction,
    [NativeActionsList.richTextFontSize]: validateRichTextAction,
    [NativeActionsList.richTextForeColor]: validateRichTextAction,
    [NativeActionsList.richTextItalic]: validateRichTextAction,
    [NativeActionsList.richTextLink]: validateRichTextAction,
    [NativeActionsList.richTextPanel]: validateRichTextAction,
    [NativeActionsList.richTextStrikethrough]: validateRichTextAction,
    [NativeActionsList.richTextUnderline]: validateRichTextAction,
    [NativeActionsList.pin]: (componentsAPI, editorPointer) =>
      !componentsAPI.layout.getValue(editorPointer, 'disablePositioning'),
    // Native Actions validators
    [NativeActionsList.settings]: validateSettingsAction,
    [NativeActionsList.help]: validateHelpAction,
    [NativeActionsList.interactions]: validateInteractionAction,
    [NativeActionsList.design]: validateDesignAction,
    [NativeActionsList.manageMenu]: validateManageMenuAction,
    [NativeActionsList.manageItems]: validateManageItemsAction,
    [NativeActionsList.changeMedia]: validateMediaAction,
    [NativeActionsList.editImage]: validateImageAction,
    [NativeActionsList.cropImage]: validateImageAction,
    [NativeActionsList.adjustImage]: validateImageAction,
    [NativeActionsList.filterImage]: validateImageAction,
    [NativeActionsList.Background]: validateBackgroundAction,
    [NativeActionsList.elements]: (componentsAPI, editorPointer) =>
      componentsAPI.getElementsData(editorPointer).showElementsPanel,
    [NativeActionsList.plugins]: validatePluginsAction,
    // Global design actions validators
    [NativeActionsList.textStyles]:
      globalDesignActions[NativeActionsList.textStyles].validator,
    [NativeActionsList.cornerStyles]:
      globalDesignActions[NativeActionsList.cornerStyles].validator,
    [NativeActionsList.fillStyles]:
      // Disable fill styles for now
      () => false,
    [NativeActionsList.spacingStyles]:
      globalDesignActions[NativeActionsList.spacingStyles].validator,
    [NativeActionsList.globalColorsStyles]:
      globalDesignActions[NativeActionsList.globalColorsStyles].validator,
    [NativeActionsList.borderStyles]:
      globalDesignActions[NativeActionsList.borderStyles].validator,
    [NativeActionsList.shadowStyles]:
      globalDesignActions[NativeActionsList.shadowStyles].validator,
  };

export const deriveDefaultEditActionsFromManifest = (
  componentsAPI: ComponentsService,
  editorPointer: EditorPointer<CompRef>,
): EditActionsDictionary<Action> => {
  return Object.fromEntries(
    Object.entries(editActionsValidators)
      .filter(([, validate]) => validate(componentsAPI, editorPointer))
      .map(([actionName]) => [actionName, { actionName }]),
  ) as EditActionsDictionary<Action>;
};
